import React, { useRef, useState, useMemo } from 'react';
import Header from '../../components/Header/Header';
import Footer from '../../components/Footer/Footer';
import { Helmet } from 'react-helmet';
import { Card } from 'primereact/card';
import { FileUpload } from 'primereact/fileupload';
import { Toast } from 'primereact/toast';
import { Button } from 'primereact/button';
import { InputText } from 'primereact/inputtext';
import { InputNumber } from 'primereact/inputnumber';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { FilterMatchMode, FilterOperator } from 'primereact/api';
import { IconField } from 'primereact/iconfield';
import { InputIcon } from 'primereact/inputicon';
import { TabView, TabPanel } from 'primereact/tabview';
import { Tag } from 'primereact/tag';
import { Tooltip } from 'primereact/tooltip';
import { Divider } from 'primereact/divider';
import { Timeline } from 'primereact/timeline';
import { Message } from 'primereact/message';
import { SelectButton } from 'primereact/selectbutton';
import { Slider } from 'primereact/slider';
import { Dropdown } from 'primereact/dropdown';
import { Accordion, AccordionTab } from 'primereact/accordion';
import axios from 'axios';
import MolstarViewerCalculation from '../../components/MolstarViewerCalculation/MolstarViewerCalculation';
import BetweennessBarChart from '../../components/BetweennessBarChart/BetweennessBarChart';
import GroupedResidueBarChart from '../../components/GroupedResidueBarChart/GroupedResidueBarChart';
import SpmProfileTable from '../../components/SpmProfileTable/SpmProfileTable';
import EgoNetworkPlot from '../../components/EgoNetworkPlot/EgoNetworkPlot';
import GenericAnalysisResult from '../../components/GenericAnalysisResult/GenericAnalysisResult';
import { Activity } from 'lucide-react';
import { InputSwitch } from 'primereact/inputswitch';


const CalculationPage = () => {

    const toast = useRef(null);
    const tableRef = useRef(null);

    const [activeIndex, setActiveIndex] = useState(0);

    const [structureId, setStructureId] = useState('');
    const [distance, setDistance] = useState(8);
    const [selectedFile, setSelectedFile] = useState(null);

    const [searchTaskId, setSearchTaskId] = useState('');

    const [loading, setIsLoading] = useState(false);
    const [resultData, setResultData] = useState(null);

    const [selectedContact, setSelectedContact] = useState(null);

    // --- ANALYSIS STATE ---
    const [analysisLoading, setAnalysisLoading] = useState(false);
    const [analysisResult, setAnalysisResult] = useState(null);
    const [startNode, setStartNode] = useState('');
    const [endNode, setEndNode] = useState('');
    const [analysisMode, setAnalysisMode] = useState(null);

    // Level 1 Path: contact scope (intra / inter / both )
    const [contactScope, setContactScope] = useState('both');

    // Add/remove ligand from analysis
    const [addLigand, setAddLigand] = useState(true);

    // SPM analysis: top-N% percentile for hub/edge classification
    const [spmPercentile, setSpmPercentile] = useState(null);

    // Separate result buckets so switching tabs doesn't clobber prior runs
    const [pathResult, setPathResult] = useState(null);       // Level 1
    const [hubsResult, setHubsResult] = useState(null);       // Level 2
    const [spmResult, setSpmResult] = useState(null);         // SPM

    // Generic results for the "other levels" (keyed by mode)
    const [genericResults, setGenericResults] = useState({});

    // Target residue for VUS resolver (level12) — reuses startNode in context
    // but exposed separately so the two tabs don't fight over the same input.
    const [vusTarget, setVusTarget] = useState('');

    // Ego network drill-in (residue selected from SPM table or betweenness chart)
    const [egoResidue, setEgoResidue] = useState(null);
    const [egoLoading, setEgoLoading] = useState(false);
    const [egoData, setEgoData] = useState(null);

    // Tab index inside the Downstream Analysis TabView
    const [analysisTab, setAnalysisTab] = useState(0);

    const [filters, setFilters] = useState({
        global: { value: null, matchMode: FilterMatchMode.CONTAINS },
        atom1: { operator: FilterOperator.AND, constraints: [{ value: null, matchMode: FilterMatchMode.CONTAINS }] },
        uniprot1: { operator: FilterOperator.AND, constraints: [{ value: null, matchMode: FilterMatchMode.CONTAINS }] },
        atom2: { operator: FilterOperator.AND, constraints: [{ value: null, matchMode: FilterMatchMode.CONTAINS }] },
        uniprot2: { operator: FilterOperator.AND, constraints: [{ value: null, matchMode: FilterMatchMode.CONTAINS }] },
        type: { operator: FilterOperator.OR, constraints: [{ value: null, matchMode: FilterMatchMode.EQUALS }] },
        distance: { operator: FilterOperator.AND, constraints: [{ value: null, matchMode: FilterMatchMode.EQUALS }] }
    });
    const [globalFilterValue, setGlobalFilterValue] = useState('');

    const STANDARD_AMINO_ACIDS = new Set([
        'ALA', 'ARG', 'ASN', 'ASP', 'CYS',
        'GLN', 'GLU', 'GLY', 'HIS', 'ILE',
        'LEU', 'LYS', 'MET', 'PHE', 'PRO',
        'SER', 'THR', 'TRP', 'TYR', 'VAL',
        'HOH'
    ]);

    const MODIFIED_RESIDUES = new Set([
        'MSE', 'SEP', 'TPO', 'PTR', 'PCA', 'HYP', 'CSO', 'KCX', 'LLP', 'TYS'
    ]);

    const onFileSelect = (e) => {
        if (e.files && e.files.length > 0) {
            setSelectedFile(e.files[0]);
            setStructureId('');
            toast.current.show({ severity: 'info', summary: 'File Selected', detail: e.files[0].name });
        }
    };

    const onClearFile = () => {
        setSelectedFile(null);
    };

    const handleExampleClick = (exampleValue) => {
        setStructureId(exampleValue);
        setSelectedFile(null);
    };

    const copyToClipboard = (text) => {
        if (!text) return;
        navigator.clipboard.writeText(text).then(() => {
            toast.current.show({ severity: 'info', summary: 'Copied', detail: 'Task ID copied to clipboard' });
        }).catch(err => {
            console.error('Failed to copy: ', err);
        });
    };

    const getExpirationDate = () => {
        const date = new Date();
        date.setDate(date.getDate() + 30);
        return date.toLocaleDateString();
    };

    const parseContacts = (contactString) => {
        if (!contactString) return [];
        const lines = contactString.split('\n').filter(line => line.trim() !== '');

        return lines.map((line, index) => {
            const tabParts = line.split('\t');

            let contactPart = tabParts[0];
            let uni1 = '-';
            let uni2 = '-';
            let summary1 = null;
            let summary2 = null;

            if (tabParts.length >= 3) {
                uni1 = tabParts[1];
                uni2 = tabParts[2];
            }
            if (tabParts.length >= 5) {
                try {
                    summary1 = JSON.parse(tabParts[3]);
                    summary2 = JSON.parse(tabParts[4]);
                } catch (e) {
                    console.error("Error parsing summary JSON", e);
                }
            }

            const parts = contactPart.trim().split(/\s+/);
            const atom1 = parts[0] || '';
            const atom2 = parts[1] || '';
            const dist = parseFloat(parts[2]) || 0;

            const resName1 = (atom1.split('/')[3] || '').toUpperCase();
            const resName2 = (atom2.split('/')[3] || '').toUpperCase();

            const getResidueType = (resName) => {
                if (STANDARD_AMINO_ACIDS.has(resName)) return 'Standard';
                if (MODIFIED_RESIDUES.has(resName)) return 'Modified';
                return 'Ligand';
            };

            const type1 = getResidueType(resName1);
            const type2 = getResidueType(resName2);

            let interactionType = 'Residue-Residue';

            if (type1 === 'Ligand' || type2 === 'Ligand') {
                interactionType = 'Ligand-Interaction';
            } else if (type1 === 'Modified' || type2 === 'Modified') {
                interactionType = 'Modification';
            }

            return {
                id: index,
                atom1: atom1,
                uniprot1: uni1,
                summary1: summary1,
                atom2: atom2,
                uniprot2: uni2,
                summary2: summary2,
                distance: dist,
                type: interactionType
            };
        });
    };

    const allContactsData = useMemo(() => {
        if (!resultData || !resultData.contacts) return null;

        const lines = resultData.contacts.split('\n').filter(line => line.trim() !== '');

        let chains = [];
        let positions = [];
        let residues = [];

        lines.forEach(line => {
            const tabParts = line.split('\t');
            const parts = tabParts[0].trim().split(/\s+/);
            if(parts.length < 2) return;

            const atom1 = parts[0].split('/');
            const atom2 = parts[1].split('/');

            chains.push(atom1[0]);
            positions.push(parseInt(atom1[1]));
            residues.push(atom1[3]);

            chains.push(atom2[0]);
            positions.push(parseInt(atom2[1]));
            residues.push(atom2[3]);
        });

        return { chains, positions, residues };
    }, [resultData]);

    // Pairs for the viewer's "Show network" button: one entry per contact line.
    // An atom is CHAIN/POS/ATOM/RESNAME, so index 2 is the anchor atom — the
    // viewer falls back to CB/CA/centroid if it isn't in the structure.
    const contactPairs = useMemo(() => {
        if (!resultData || !resultData.contacts) return [];

        return resultData.contacts.split('\n')
            .filter(line => line.trim() !== '')
            .map(line => {
                const parts = line.split('\t')[0].trim().split(/\s+/);
                if (parts.length < 2) return null;

                const a = parts[0].split('/');
                const b = parts[1].split('/');
                if (a.length < 2 || b.length < 2) return null;

                const aPos = parseInt(a[1], 10);
                const bPos = parseInt(b[1], 10);
                if (Number.isNaN(aPos) || Number.isNaN(bPos)) return null;

                return {
                    aChain: a[0], aPos, aAtom: a[2],
                    bChain: b[0], bPos, bAtom: b[2],
                    distance: parseFloat(parts[2]),
                };
            })
            .filter(Boolean);
    }, [resultData]);


    const validateStructureInput = (id) => {
        const pdbRegex = /^[0-9][A-Za-z0-9]{3}$/;
        const uniprotRegex = /^([OPQ][0-9][A-Z0-9]{3}[0-9]|[A-NR-Z][0-9]([A-Z][A-Z0-9]{2}[0-9]){1,2})(-[0-9]+)?$/i;

        if (pdbRegex.test(id)) return true;
        if (uniprotRegex.test(id)) return true;

        return false;
    };

    const handleViewStructure = (rowData) => {
        try {
            const p1 = rowData.atom1.split('/');
            const p2 = rowData.atom2.split('/');

            const chain1 = p1[0];
            const pos1 = parseInt(p1[1]);
            const res1 = p1[3];

            const chain2 = p2[0];
            const pos2 = parseInt(p2[1]);
            const res2 = p2[3];

            setSelectedContact({
                chains: [chain1, chain2],
                positions: [pos1, pos2],
                residues: [res1, res2],
                distance: rowData.distance
            });

            const element = document.getElementById('molstar-viewer-section');
            if (element) element.scrollIntoView({ behavior: 'smooth' });

        } catch (err) {
            console.error("Error parsing atom string for visualization", err);
            toast.current.show({ severity: 'error', summary: 'Visualization Error', detail: 'Could not parse residue data' });
        }
    };

    const handleResetView = () => {
        setSelectedContact(null);
    };

    // --- API CALLS ---

    async function retrieveTaskByid() {
        if(!searchTaskId) return;
        setIsLoading(true);
        setSelectedContact(null);
        resetAnalysisState(); // Reset analysis

        const query = `
            query getTask($taskId: String!) {
                getTask(taskId: $taskId) {
                    contacts
                    taskId
                    structure
                }
            }
        `;

        try {
            const response = await axios.post('https://api.atlantis.bioinfolab.sns.it/graphql', {
                query: query,
                variables: { taskId: searchTaskId }
            });

            const data = response.data;
            if (data.errors) {
                toast.current.show({ severity: 'error', summary: 'Error', detail: data.errors[0].message });
            } else if (data.data && data.data.getTask) {
                setResultData(data.data.getTask);
                toast.current.show({ severity: 'success', summary: 'Found', detail: 'Task retrieved successfully' });
            } else {
                toast.current.show({ severity: 'warn', summary: 'Not Found', detail: 'No task found with this ID' });
            }
        } catch (error) {
            console.error(error);
            toast.current.show({ severity: 'error', summary: 'Error', detail: 'Network error' });
        } finally {
            setIsLoading(false);
        }
    }

    async function contactCalculation() {
    setIsLoading(true);
    setSelectedContact(null);
    resetAnalysisState();

    const query = `
        query contactCalculation($structure: String!, $distance: Int, $file: Upload) {
            contactCalculation(structure: $structure, distance: $distance, file: $file) {
                contacts
                taskId
                structure
            }
        }
    `;

    try {
        let response;

        if (selectedFile) {
            // Con file → multipart/form-data
            const operations = {
                query,
                variables: {
                    structure: "uploaded_file",
                    distance: parseInt(distance),
                    file: null
                }
            };
            const formData = new FormData();
            formData.append('operations', JSON.stringify(operations));
            formData.append('map', JSON.stringify({ "0": ["variables.file"] }));
            formData.append('0', selectedFile);

            response = await axios.post(
                'https://api.atlantis.bioinfolab.sns.it/graphql',
                formData,
                { headers: { 'Content-Type': 'multipart/form-data' } }
            );
        } else {
            // Senza file → JSON normale
            response = await axios.post(
                'https://api.atlantis.bioinfolab.sns.it/graphql',
                {
                    query,
                    variables: {
                        structure: structureId,
                        distance: parseInt(distance),
                        file: null
                    }
                }
            );
        }

        const data = response.data;
        if (data.errors) {
            console.error("GraphQL Errors:", data.errors);
            toast.current.show({ severity: 'error', summary: 'Error', detail: data.errors[0].message });
        } else if (data.data && data.data.contactCalculation) {
            setResultData(data.data.contactCalculation);
            toast.current.show({ severity: 'success', summary: 'Success', detail: 'Calculation complete' });
        }
    } catch (error) {
        console.error("Network Error:", error);
        toast.current.show({ severity: 'error', summary: 'Error', detail: 'Failed to connect to server' });
    } finally {
        setIsLoading(false);
    }
}

    async function runGraphAnalysis(mode, options = {}) {
        if (!resultData || !resultData.taskId) return;
        if (mode === 'level1' && (!startNode || !endNode)) {
            toast.current.show({ severity: 'warn', summary: 'Missing Input', detail: 'Please specify Start and End residues (e.g., A/10).' });
            return;
        }

        setAnalysisLoading(true);
        setAnalysisMode(mode);
        setAnalysisResult(null);

        // Uses the existing structure ID or a placeholder if uploaded
        const pdbIdVal = structureId || 'UPLOADED';

        const query = `
            query getTaskAnalyzeContactsGraph($taskId: String!, $input: GraphAnalysisInput!) {
                getTaskAnalyzeContactsGraph(taskId: $taskId, inputData: $input) {
                    resultType
                    totalDistance
                    path {
                    name
                    annotations {
                      has_clinvar: hasClinvar
                      has_intra_contact: hasIntraContact
                      has_af_intra_contact: hasAfIntraContact
                      has_inter_contact: hasInterContact
                      has_af_inter_contact: hasAfInterContact
                      has_ligand: hasLigand
                      is_functional: isFunctional
                      has_site: hasSite
                    }
                  }
                  tableData
                }
            }
        `;

        // Merge inputs: defaults can be overridden by `options` for programmatic
        // calls (e.g. the ego-network drill-in uses options.startResidue).
        const variables = {
            taskId: resultData.taskId,
            input: {
                pdbId: pdbIdVal,
                mode: mode,
                startResidue: (options.startResidue ?? startNode).toString().trim(),
                endResidue: (options.endResidue ?? endNode).toString().trim(),
                contactScope: options.contactScope ?? contactScope,
                addLigand:  options.addLigand ?? addLigand,
                spmPercentile: options.spmPercentile ?? spmPercentile,
            }
        };

        try {
            const response = await axios.post('https://api.atlantis.bioinfolab.sns.it/graphql', {
                query: query,
                variables: variables
            });

            const data = response.data;
            if (data.errors) {
                toast.current.show({ severity: 'error', summary: 'Analysis Error', detail: data.errors[0].message });
                return null;
            }
            if (data.data && data.data.getTaskAnalyzeContactsGraph) {
                const result = data.data.getTaskAnalyzeContactsGraph;
                setAnalysisResult(result);


                // Route to the correct bucket (so the tabs remember their state)
                if (mode === 'level1') setPathResult(result);
                else if (mode === 'level2') setHubsResult(result);
                else if (mode === 'spm') setSpmResult(result);
                else {
                    // level4 / level5 / level6 / level7 / level9 / level12 / communities
                    setGenericResults((prev) => ({ ...prev, [mode]: result }));
                }

                if (!options.silent) {
                    toast.current.show({ severity: 'success', summary: 'Success', detail: 'Analysis complete' });
                }
                return result;
            }
            return null;
        } catch (error) {
            console.error(error);
            toast.current.show({ severity: 'error', summary: 'Error', detail: 'Analysis failed' });
            return null;
        } finally {
            setAnalysisLoading(false);
        }
    }

    function openEgoNetwork(residue) {
    if (!residue || !spmResult?.tableData) return;

    setEgoResidue(residue);
    setEgoLoading(false);
    setEgoData(null);

    const targetRow = spmResult.tableData.find(
        (row) => row && row.col1 === String(residue) && row.col1 !== '__summary__'
    );

    if (!targetRow || !Array.isArray(targetRow.neighbors)) {
        setEgoData({ neighbors: [] });
        return;
    }

    const spmByResidue = new Map();
    spmResult.tableData.forEach((row) => {
        if (row && row.col1 && row.col1 !== '__summary__') {
            spmByResidue.set(String(row.col1), {
                betweenness: Number(row.col3),
                isHub: row.col5 === 'Yes',
            });
        }
    });

    const neighbors = targetRow.neighbors.map((n) => {
        const spm = spmByResidue.get(n.position) || {};
        const summary = n.summary || {};
        return {
            position: n.position,
            distance: n.distance,
            betweenness: spm.betweenness,
            isHub: !!spm.isHub,
            amClass: n.am_class || null,
            annotations: {
                summary,
                has_ligand: summary.has_ligand,
                is_functional: summary.is_functional,
            },
            rawLabel: null,
            features: null,
        };
    });

    setEgoData({ neighbors });
}

    /** Clear every analysis-related piece of state. */
    function resetAnalysisState() {
        setAnalysisResult(null);
        setPathResult(null);
        setHubsResult(null);
        setSpmResult(null);
        setGenericResults({});
        setVusTarget('');
        setEgoResidue(null);
        setEgoData(null);
        setEgoLoading(false);
    }


    // --- UI HELPERS ---

    const handleFormSubmit = (e) => {
        e.preventDefault();
        if (selectedFile) { contactCalculation(); return; }
        if (!structureId) { toast.current.show({ severity: 'warn', summary: 'Attention', detail: 'Please provide a Structure ID or upload a file.' }); return; }
        if (!validateStructureInput(structureId)) { toast.current.show({ severity: 'error', summary: 'Invalid ID', detail: 'Please enter a valid PDB ID (4 chars) or UniProt Accession.' }); return; }
        contactCalculation();
    };

    const resetSearch = () => {
        setResultData(null);
        setStructureId('');
        setSelectedFile(null);
        setSearchTaskId('');
        setSelectedContact(null);
        resetAnalysisState();
        setStartNode('');
        setEndNode('');
        setGlobalFilterValue('');
        setFilters({
            global: { value: null, matchMode: FilterMatchMode.CONTAINS },
            atom1: { operator: FilterOperator.AND, constraints: [{ value: null, matchMode: FilterMatchMode.CONTAINS }] },
            uniprot1: { operator: FilterOperator.AND, constraints: [{ value: null, matchMode: FilterMatchMode.CONTAINS }] },
            atom2: { operator: FilterOperator.AND, constraints: [{ value: null, matchMode: FilterMatchMode.CONTAINS }] },
            uniprot2: { operator: FilterOperator.AND, constraints: [{ value: null, matchMode: FilterMatchMode.CONTAINS }] },
            type: { operator: FilterOperator.OR, constraints: [{ value: null, matchMode: FilterMatchMode.EQUALS }] },
            distance: { operator: FilterOperator.AND, constraints: [{ value: null, matchMode: FilterMatchMode.EQUALS }] }
        });
    };

    const onGlobalFilterChange = (e) => {
        const value = e.target.value;
        let _filters = { ...filters };
        _filters['global'].value = value;
        setFilters(_filters);
        setGlobalFilterValue(value);
    };

    const clearFilter = () => {
        setFilters({
            global: { value: null, matchMode: FilterMatchMode.CONTAINS },
            atom1: { operator: FilterOperator.AND, constraints: [{ value: null, matchMode: FilterMatchMode.CONTAINS }] },
            uniprot1: { operator: FilterOperator.AND, constraints: [{ value: null, matchMode: FilterMatchMode.CONTAINS }] },
            atom2: { operator: FilterOperator.AND, constraints: [{ value: null, matchMode: FilterMatchMode.CONTAINS }] },
            uniprot2: { operator: FilterOperator.AND, constraints: [{ value: null, matchMode: FilterMatchMode.CONTAINS }] },
            type: { operator: FilterOperator.OR, constraints: [{ value: null, matchMode: FilterMatchMode.EQUALS }] },
            distance: { operator: FilterOperator.AND, constraints: [{ value: null, matchMode: FilterMatchMode.EQUALS }] }
        });
        setGlobalFilterValue('');
    };

    const exportCSV = () => tableRef.current.exportCSV();

    const exportExcel = async () => {
        const dataToExport = parseContacts(resultData.contacts).map(item => ({
            type: item.type,
            atom1: item.atom1,
            uniprot1: item.uniprot1,
            atom2: item.atom2,
            uniprot2: item.uniprot2,
            distance: item.distance
        }));
        const xlsx = await import('xlsx');
        const worksheet = xlsx.utils.json_to_sheet(dataToExport);
        const workbook = { Sheets: { data: worksheet }, SheetNames: ['data'] };
        const excelBuffer = xlsx.write(workbook, { bookType: 'xlsx', type: 'array' });
        saveAsExcelFile(excelBuffer, 'contacts_data');
    };

    const saveAsExcelFile = (buffer, fileName) => {
        import('file-saver').then((module) => {
            if (module && module.default) {
                const EXCEL_TYPE = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8';
                const EXCEL_EXTENSION = '.xlsx';
                const data = new Blob([buffer], { type: EXCEL_TYPE });
                module.default.saveAs(data, fileName + '_export_' + new Date().getTime() + EXCEL_EXTENSION);
            }
        });
    };

    const exportPdf = () => {
        const dataToExport = parseContacts(resultData.contacts);
        import('jspdf').then((jsPDF) => {
            import('jspdf-autotable').then(() => {
                const doc = new jsPDF.default('p', 'pt');
                const exportColumns = [
                    { title: 'Type', dataKey: 'type' },
                    { title: 'Atom 1', dataKey: 'atom1' },
                    { title: 'Mapped ID 1', dataKey: 'uniprot1' },
                    { title: 'Atom 2', dataKey: 'atom2' },
                    { title: 'Mapped ID 2', dataKey: 'uniprot2' },
                    { title: 'Distance (Å)', dataKey: 'distance' }
                ];
                doc.autoTable(exportColumns, dataToExport);
                doc.save('contacts_data.pdf');
            });
        });
    };

    const renderNumericFilter = (options) => (
        <InputText
            type="number"
            value={options.value || ''}
            onChange={(e) => options.filterCallback(e.target.value, options.index)}
            placeholder="Enter value"
            className="p-column-filter"
        />
    );

    const typeBodyTemplate = (rowData) => {
        let severity = 'info';
        if (rowData.type === 'Ligand-Interaction') severity = 'danger';
        else if (rowData.type === 'Modification') severity = 'warning';
        return (<Tag value={rowData.type} severity={severity} rounded></Tag>);
    };

    const renderTags = (summary) => {
        if (!summary) return null;

        return (
          <div className="flex gap-1 justify-content-center">
            {summary.is_functional && (
             <>
               <Tooltip target=".is_functional" />
               <i className="is_functional pi pi-cog p-1" style={{ color: '#708090' }} data-pr-tooltip="Functional Significance"
                data-pr-position="right"
                data-pr-at="right+5 top"
                data-pr-my="left center-2"></i>
             </>
            )}
            {summary.has_clinvar && (
             <>
              <Tooltip target=".has_clinvar" />
              <i className="has_clinvar pi pi-shield p-1" style={{ color: '#708090' }} data-pr-tooltip="Has ClinVar variant"
                data-pr-position="right"
                data-pr-at="right+5 top"
                data-pr-my="left center-2"></i>
             </>
            )}
            {summary.has_intra_contact && (
             <>
              <Tooltip target=".has_intra_contact" />
              <i className="has_intra_contact pi pi-globe p-1" style={{ color: '#708090' }} data-pr-tooltip="Has Intra-protein Contact"  data-pr-position="right"
                data-pr-at="right+5 top"
                data-pr-my="left center-2"></i>
             </>
            )}
             {summary.has_af_intra_contact && (
             <>
              <Tooltip target=".has_af_intra_contact" />
              <i className="has_af_intra_contact pi pi pi-star p-1" style={{ color: '#708090' }} data-pr-tooltip="Has AlphaFold Intra-protein Contact"  data-pr-position="right"
                data-pr-at="right+5 top"
                data-pr-my="left center-2"></i>
             </>
            )}
              {summary.has_inter_contact && (
              <>
              <Tooltip target=".has_inter_contact" />
              <i className="has_inter_contact pi pi-sitemap p-1" style={{ color: '#708090' }} data-pr-tooltip="Has Inter-protein Interaction"
               data-pr-position="right"
                data-pr-at="right+5 top"
                data-pr-my="left center-2"></i>
              </>
            )}
            {summary.has_af_inter_contact && (
             <>
              <Tooltip target=".has_af_inter_contact" />
              <i className="has_af_inter_contact pi pi-star-fill p-1" style={{ color: '#708090' }} data-pr-tooltip="Has AlphaFold Inter-protein Contact"  data-pr-position="right"
                data-pr-at="right+5 top"
                data-pr-my="left center-2"></i>
             </>
            )}
            { summary.has_ligand  && (
              <>
              <Tooltip target=".has_ligand" />
              <i className="has_ligand pi pi-ethereum p-1" style={{ color: '#708090' }} data-pr-tooltip="Has Ligand Contact"
               data-pr-position="right"
                data-pr-at="right+5 top"
                data-pr-my="left center-2"></i>
              </>
            )}
          </div>
        );
      };

    const tags1BodyTemplate = (rowData) => renderTags(rowData.summary1);
    const tags2BodyTemplate = (rowData) => renderTags(rowData.summary2);

    const viewBodyTemplate = (rowData) => {
        return (
            <Button
                icon="pi pi-eye"
                className="p-button-rounded p-button-text p-button-info"
                onClick={() => handleViewStructure(rowData)}
                tooltip="View this contact"
            />
        );
    };

    // --- TEMPLATE FOR TIMELINE ---
    const customizedMarker = (item, index) => {
    if (index === 0) return <i className="pi pi-circle-fill text-danger" style={{ fontSize: '1.2rem' }}></i>;
    if (pathResult && pathResult.path && index === pathResult.path.length - 1) return <i className="pi pi-check-circle text-success" style={{ fontSize: '1.2rem' }}></i>;
    return <i className="pi pi-circle text-secondary" style={{ fontSize: '0.8rem' }}></i>;
  };

    const customizedContent = (item) => {
        return (
            <Card className="mb-2 shadow-sm border-0" style={{ padding: '0.5rem' }}>
                <div className="d-flex flex-column">
                    <span className="fw-bold text-dark mb-1">{item.name}</span>
                    <div className="flex gap-2">
                        {renderTags(item.annotations)}
                    </div>
                </div>
            </Card>
        );
    };

    const tableHeader = (
        <div className="row align-items-center">
            <div className="col-12 col-md-6 mb-2 mb-md-0 d-flex align-items-center gap-2">
                <IconField iconPosition="left">
                    <InputIcon className="pi pi-search" />
                    <InputText type="search" value={globalFilterValue} onChange={onGlobalFilterChange} placeholder="Global Search" />
                </IconField>
                <Button type="button" icon="pi pi-filter-slash" label="Clear" outlined onClick={clearFilter} />
            </div>
            <div className="col-12 col-md-6 d-flex justify-content-end align-items-center gap-2">
                <Button icon="pi pi-file" rounded onClick={exportCSV} tooltip="Export CSV" />
                <Button icon="pi pi-file-excel" severity="success" rounded onClick={exportExcel} tooltip="Export Excel" />
                <Button icon="pi pi-file-pdf" severity="warning" rounded onClick={exportPdf} tooltip="Export PDF" />
                <Button icon="pi pi-refresh" severity="info" rounded onClick={resetSearch} tooltip="New Search" />
            </div>
        </div>
    );

    return (
        <>
            <Helmet>
                <title>Atlantis | Contact Calculation</title>
            </Helmet>
            <Header />
            <section className="px-5 py-10">
                <div className="container">
                    <div className="row justify-content-center">
                        <div className="col-12">
                           <h1 className="display-5 fw-bold mb-4 text-center text-sm-start">
                          Contact Calculation
                          <sup>
                            <a href="/help/#calculation" target="_blank" rel="noopener noreferrer" style={{ textDecoration: 'none' }}>
                                <Button
                                    icon="pi pi-info"
                                    text
                                    rounded
                                    size="small"
                                    className="btn-dialog"
                                    tooltip="Learn calculation method"
                                />
                            </a>
                          </sup>
                        </h1>
                            <Toast ref={toast}></Toast>

                            {!resultData ? (
                                <Card className="shadow-sm">
                                    <TabView activeIndex={activeIndex} onTabChange={(e) => setActiveIndex(e.index)}>
                                        <TabPanel header="New Calculation">
                                            <form onSubmit={handleFormSubmit} className="mt-2">
                                                <div className="row g-3">
                                                    <div className="col-12 col-md">
                                                        <label htmlFor="structure" className="form-label fw-bold">Structure ID</label>
                                                        <InputText
                                                            id="structure"
                                                            value={structureId}
                                                            onChange={(e) => {
                                                                setStructureId(e.target.value);
                                                                if (e.target.value) setSelectedFile(null);
                                                            }}
                                                            placeholder="PDB ID (e.g. 10gs) or UniProt (e.g. P09471)"
                                                            className="w-100 p-inputtext-lg"
                                                            disabled={!!selectedFile}
                                                        />
                                                        <div className="mt-1 text-muted small">
                                                            Examples:
                                                            {['10gs', '3ama', 'P09471'].map((item) => (
                                                                <span key={item} onClick={() => handleExampleClick(item)} style={{ cursor: 'pointer' }} className="text-primary ms-2 text-decoration-underline">
                                                                    {item}
                                                                </span>
                                                            ))}
                                                        </div>
                                                    </div>
                                                    <div className="col-6 col-md-auto" style={{ minWidth: '120px' }}>
                                                        <label htmlFor="distance" className="form-label fw-bold">Distance (Å)</label>
                                                        <InputNumber inputId="distance" value={distance} onValueChange={(e) => setDistance(e.value)} min={1} max={8} showButtons className="w-100" inputClassName="p-inputtext-lg" />
                                                    </div>
                                                    <div className="col-6 col-md-auto">
                                                        <label className="form-label fw-bold d-block">Or Upload (.cif)</label>
                                                        <div className="d-flex align-items-center">
                                                            <FileUpload mode="basic" name="file" accept=".cif,.pdb" maxFileSize={5000000} chooseLabel={selectedFile ? "Change" : "Browse"} customUpload={true} onSelect={onFileSelect} onClear={onClearFile} auto={true} className="p-button-outlined p-button-lg" />
                                                        </div>
                                                        {selectedFile && <div className="small text-success fw-bold mt-1 text-truncate" style={{ maxWidth: '150px' }}>{selectedFile.name}</div>}
                                                    </div>
                                                    <div className="col-12 col-md-auto">
                                                        <label className="form-label d-block opacity-0">Submit</label>
                                                        <Button label={loading ? "Calculating..." : "Submit"} icon={loading ? "pi pi-spin pi-spinner" : "pi pi-play"} type="submit" disabled={loading} className="p-button-lg w-100" />
                                                    </div>
                                                </div>
                                            </form>
                                        </TabPanel>
                                        <TabPanel header="Retrieve by Task ID">
                                            <div className="row g-3 align-items-end mt-2">
                                                <div className="col-12 col-md-8">
                                                    <label htmlFor="taskid" className="form-label fw-bold">Task ID</label>
                                                    <InputText id="taskid" value={searchTaskId} onChange={(e) => setSearchTaskId(e.target.value)} placeholder="Enter UUID (e.g. 967c5b04-91f2...)" className="w-100 p-inputtext-lg" />
                                                </div>
                                                <div className="col-12 col-md-4">
                                                    <Button label={loading ? "Searching..." : "Retrieve Results"} icon="pi pi-search" onClick={retrieveTaskByid} disabled={!searchTaskId || loading} className="p-button-lg w-100 p-button-outlined" />
                                                </div>
                                            </div>
                                        </TabPanel>
                                    </TabView>

                            <div className="mb-2 mt-3 text-center text-md-start">
                                <p className="text-secondary">
                                    To begin, input a <strong>PDB ID</strong> (e.g., <em>10gs</em>) or <strong>UniProt Accession</strong> (e.g., <em>P09471</em>),
                                    or upload your own <strong>.cif file</strong>. Set the desired <strong>Distance</strong> threshold (default 8Å) to define the contact range.
                                    If you have a previous analysis, switch to the "Retrieve by Task ID" tab to reload your results.
                                </p>
                            </div>
                            </Card>
                            ) : (
                                <div className="fade-in">
                                    <div className="mb-4 p-3 bg-light border-round relative">
                                        <div className="row align-items-center">
                                            <div className="col-md-6 d-flex align-items-center gap-2">
                                                <span className="font-bold">Task ID:</span>
                                                <span className="text-primary font-mono">{resultData.taskId}</span>
                                                <Button icon="pi pi-copy" rounded text severity="secondary" size="small" tooltip="Copy Task ID" onClick={() => copyToClipboard(resultData.taskId)} />
                                            </div>
                                            <div className="col-md-6 text-md-end">
                                                <strong>Source:</strong> {structureId ? structureId : "Uploaded File / Task Retrieval"}
                                            </div>
                                        </div>
                                        <div className="mt-2 mb-3 text-xs text-500 d-flex align-items-center">
                                            <i className="pi pi-info-circle mr-1"></i>
                                            <span>&nbsp;Results will be stored until {getExpirationDate()}.</span>
                                        </div>
                                        <p className="text-500">
                                            The table below displays all atomic contacts identified within the <strong>{distance}Å</strong> threshold.
                                            Interactions are categorized by type (e.g., <Tag value="Residue-Residue" severity="info" rounded></Tag>,
                                            <Tag value="Ligand-Interaction" severity="danger" rounded></Tag>,  <Tag value="Modification" severity="warning" rounded></Tag>).
                                        </p>
                                    </div>

                                    <DataTable
                                        ref={tableRef}
                                        value={parseContacts(resultData.contacts)}
                                        paginator
                                        rows={5}
                                        header={tableHeader}
                                        filters={filters}
                                        onFilter={(e) => setFilters(e.filters)}
                                        globalFilterFields={['atom1', 'uniprot1', 'atom2', 'uniprot2', 'distance', 'type']}
                                        stripedRows
                                        showGridlines
                                        removableSort
                                        className="nobreak"
                                        tableStyle={{ minWidth: '70rem' }}
                                    >
                                        <Column field="type" header="Type" sortable filter filterPlaceholder="Search Type" body={typeBodyTemplate} style={{ width: '160px' }} />
                                        <Column field="atom1" header="Atom 1" sortable filter filterPlaceholder="Search Atom 1" />
                                        <Column field="uniprot1" header="Mapped ID 1" sortable filter filterPlaceholder="Search ID" style={{color: 'var(--blue-600)'}}/>
                                        <Column header="Tags 1" body={tags1BodyTemplate} style={{ width: '120px', textAlign: 'center' }} />
                                        <Column field="atom2" header="Atom 2" sortable filter filterPlaceholder="Search Atom 2" />
                                        <Column field="uniprot2" header="Mapped ID 2" sortable filter filterPlaceholder="Search ID" style={{color: 'var(--blue-600)'}}/>
                                        <Column header="Tags 2" body={tags2BodyTemplate} style={{ width: '120px', textAlign: 'center' }} />
                                        <Column field="distance" header="Dist. (Å)" sortable dataType="numeric" filter filterElement={renderNumericFilter} body={(rowData) => <strong>{rowData.distance}</strong>} style={{ width: '100px' }} />
                                        <Column header="3D View" body={viewBodyTemplate} style={{ width: '90px', textAlign: 'center' }} />
                                    </DataTable>
                                </div>
                            )}
                        </div>
                    </div>

                    {resultData && (
                        <>
                            <div id="molstar-viewer-section" className="row justify-content-center mt-5 fade-in">
                                <div className="col-12">
                                    <Card title="Structure Visualization">
                                        <div className="row">
                                            <div className="col-md-3 mb-3">
                                                <div className="bg-light rounded-3 p-3 ">
                                                    <h5 className="mb-3">Legend</h5>
                                                    <div className="d-flex align-items-center mb-2">
                                                        <div style={{ width: '20px', height: '20px', backgroundColor: '#F0E68C', borderRadius: '50%', marginRight: '10px', border: '1px solid #ccc' }}></div>
                                                        <small className="text-secondary">Selected/Mapped Residues</small>
                                                    </div>

                                                    <hr />

                                                    <p className="small text-500 mb-2">
                                                        {selectedContact
                                                            ? "Visualizing specific contact selected from table."
                                                            : "Visualizing all residues involved in contacts."
                                                        }
                                                    </p>

                                                    {selectedContact && (
                                                        <Button
                                                            label="Show All Contacts"
                                                            icon="pi pi-globe"
                                                            className="p-button-sm p-button-outlined w-100 mb-3"
                                                            onClick={handleResetView}
                                                        />
                                                    )}

                                                    <p className="small text-500 mt-auto">
                                                        Use mouse to rotate (Left Click), zoom (Scroll), and pan (Right Click).
                                                        <br/><br/>
                                                        More features available in <a href="https://molstar.org/viewer-docs/" target="_blank" rel="noreferrer">Mol* Docs</a>.
                                                    </p>
                                                </div>
                                            </div>
                                            <div className="col-md-9">
                                                <div className="viewer-box border rounded" style={{ height: '600px', position: 'relative' }}>
                                                    <MolstarViewerCalculation
                                                        cifString={resultData.structure}
                                                        positions={selectedContact ? selectedContact.positions : (allContactsData ? allContactsData.positions : [])}
                                                        chain={selectedContact ? selectedContact.chains : (allContactsData ? allContactsData.chains : [])}
                                                        residues={selectedContact ? selectedContact.residues : (allContactsData ? allContactsData.residues : [])}

                                                        distance={selectedContact ? selectedContact.distance : null}
                                                        pdbId={structureId || 'Structure'}
                                                        rowType="main"
                                                        contactPairs={contactPairs}
                                                    />
                                                </div>
                                            </div>
                                        </div>
                                    </Card>
                                </div>
                            </div>

                            {/* --- SECTION: Pathfinder - Structural Graph Analysis --- */}
                            <div className="row justify-content-center fade-in my-7">
                                <div className="col-12">
                                    <div className="d-flex justify-content-between align-items-start flex-wrap gap-3 mb-4">
                                        <div>
                                         <h2 className="text-2xl font-bold mb-1">
                                            <Activity className="me-3" size={40} />
                                             Pathfinder - Structural Graph Analysis
                                             <sup>
                                                <a href="/help/#downgrah" target="_blank" rel="noopener noreferrer" style={{ textDecoration: 'none' }}>
                                                    <Button
                                                        icon="pi pi-info"
                                                        text
                                                        rounded
                                                        size="small"
                                                        className="btn-dialog"
                                                        tooltip="Learn Pathfinder"
                                                    />
                                                </a>
                                              </sup>
                                          </h2>
                                            <p className="text-secondary mb-0">Advanced graph-theoretic analyses on the contact network above.</p>
                                        </div>
                                        <div className="d-flex align-items-center gap-2">
                                            <span className="text-secondary small">Contact scope</span>
                                            <SelectButton
                                                value={contactScope}
                                                onChange={(e) => e.value && setContactScope(e.value)}
                                                options={[
                                                    { label: 'Intra', value: 'intra' },
                                                    { label: 'Inter', value: 'inter' },
                                                    { label: 'Both', value: 'both' }
                                                ]}
                                            />
                                            <span className="text-secondary small">Add Ligand</span>
                                             <InputSwitch checked={addLigand} onChange={(e) => setAddLigand(e.value)} />
                                        </div>
                                    </div>

                                    <Card className="shadow-1">
                                        <TabView activeIndex={analysisTab} onTabChange={(e) => setAnalysisTab(e.index)}>

                                            {/* ============================================= */}
                                            {/* TAB 1 — PHYSICAL PATH (Level 1)               */}
                                            {/* ============================================= */}
                                            <TabPanel header="Physical Path" leftIcon="pi pi-directions me-2">
                                                <div className="row g-3">
                                                    <div className="col-md-4">
                                                        <label className="form-label fw-bold">Start residue</label>
                                                        <InputText
                                                            className="w-100"
                                                            placeholder="e.g. P04001_16"
                                                            value={startNode}
                                                            onChange={(e) => setStartNode(e.target.value)}
                                                        />
                                                    </div>
                                                    <div className="col-md-4">
                                                        <label className="form-label fw-bold">End residue</label>
                                                        <InputText
                                                            className="w-100"
                                                            placeholder="e.g. P04001_160"
                                                            value={endNode}
                                                            onChange={(e) => setEndNode(e.target.value)}
                                                        />
                                                    </div>
                                                    <div className="col-md-4 d-flex align-items-end">
                                                        <Button
                                                            className="w-100"
                                                            label="Calculate path"
                                                            icon="pi pi-directions"
                                                            onClick={() => runGraphAnalysis('level1')}
                                                            loading={analysisLoading && analysisMode === 'level1'}
                                                        />
                                                    </div>
                                                </div>

                                                <p className="text-secondary small mt-3 mb-0">
                                                    Scope <strong>{contactScope}</strong>:{' '}
                                                    {contactScope === 'intra' && 'path restricted to intra-chain contacts.'}
                                                    {contactScope === 'inter' && 'path uses only inter-chain contacts.'}
                                                    {contactScope === 'both' && 'path can cross chain boundaries via any contact.'}
                                                </p>

                                                {/* Path result */}
                                                {pathResult && (
                                                    <div className="mt-4 animate__animated animate__fadeIn">
                                                        <Divider />
                                                        {pathResult.resultType === 'path' && pathResult.path && (
                                                            <>
                                                                <div className="d-flex justify-content-between align-items-center mb-3">
                                                                    <h4 className="mb-0 text-primary">Pathway topology</h4>
                                                                    <Tag
                                                                        value={`${Number(pathResult.totalDistance || 0).toFixed(2)} Å`}
                                                                        severity="info"
                                                                        icon="pi pi-arrows-h"
                                                                    />
                                                                </div>
                                                                <Timeline
                                                                    value={pathResult.path}
                                                                    align="alternate"
                                                                    marker={customizedMarker}
                                                                    content={customizedContent}
                                                                />
                                                            </>
                                                        )}
                                                        {pathResult.resultType === 'text' && (
                                                            <Message
                                                                severity="warn"
                                                                text={pathResult.tableData?.message || pathResult.text || 'No path found.'}
                                                                className="w-full"
                                                            />
                                                        )}
                                                    </div>
                                                )}
                                            </TabPanel>

                                            {/* ============================================= */}
                                            {/* TAB 2 — SPM ANALYSIS                          */}
                                            {/* ============================================= */}
                                            <TabPanel header="Structural Hubs/SPM" leftIcon="pi pi-sitemap me-2">
                                                {/* Context / methodology block */}
                                                 <div className="card card-spm bg-light border-0 p-3 mb-4 text-start">

                                                <h5 className="mb-3">
                                                  <i className="pi pi-info-circle me-2 text-primary" />
                                                  What is Shortest Path Mapping?
                                                </h5>

                                                <p className="mb-3 small">
                                                  SPM ranks every residue by <strong>degree</strong> and
                                                   <strong> betweenness centrality</strong> —
                                                  how many shortest paths in the contact graph pass through it.
                                                  The top-N% residues are then classified as{' '}
                                                  <strong className="text-warning-emphasis">SPM hubs</strong>, and the top-N% edges as{' '}
                                                  <strong className="text-primary">SPM edges</strong>.
                                                </p>

                                                <p className="mb-2">
                                                  <Tag
                                                    value="SPM hubs"
                                                    severity="warning"
                                                    icon="pi pi-star-fill"
                                                    rounded
                                                  />
                                                  <span className="small text-secondary ms-2">
                                                    Residues that sit on many structural communication paths —
                                                    likely <em>allosteric bottlenecks</em> or folding cores.
                                                  </span>
                                                </p>

                                                <p>
                                                  <Tag
                                                    value="SPM edges"
                                                    severity="info"
                                                    icon="pi pi-link"
                                                    rounded
                                                  />
                                                  <span className="small text-secondary ms-2">
                                                    Contacts carrying high path traffic — breaking one of
                                                    them disconnects distant parts of the structure.
                                                  </span>
                                                </p>

                                              </div>

                                                <div className="row g-3 align-items-end">
                                                    <div className="col-md-6">
                                                        <label className="form-label fw-bold d-flex justify-content-between">
                                                            <span>Top-N% </span>
                                                        </label>
                                                        <Dropdown
                                                            className="w-100"
                                                            value={spmPercentile}
                                                            onChange={(e) => setSpmPercentile(e.value)}
                                                            options={[
                                                                { label: 'Top 5%', value: 5 },
                                                                { label: 'Top 10%', value: 10 },
                                                                { label: 'Top 20%', value: 20 },
                                                                { label: 'Top 30%', value: 30 },
                                                            ]}
                                                            placeholder="Choose a cutoff"
                                                        />
                                                    </div>
                                                    <div className="col-md-3">
                                                        <Button
                                                            className="w-100"
                                                            label="Identify hubs"
                                                            icon="pi pi-share-alt"
                                                            onClick={() => runGraphAnalysis('spm', { spmPercentile })}
                                                            loading={analysisLoading && analysisMode === 'spm'}
                                                        />
                                                    </div>
                                                </div>

                                                {spmResult && spmResult.resultType === 'table' && Array.isArray(spmResult.tableData) && (
                                                    <div className="mt-4 animate__animated animate__fadeIn">
                                                        <Divider />

                                                        {/* Per-residue bar charts — three lenses on the SPM result */}
                                                        <div className="row g-3 mb-4">
                                                           {/* Contacts per residue — highlighting contacts to other SPM hubs (col8) */}
                                                        <div className="col-md-4">
                                                            <GroupedResidueBarChart
                                                                data={spmResult.tableData
                                                                    .filter((r) => r && r.col1 !== '__summary__')
                                                                    .map((r) => ({
                                                                        position: r.col1,
                                                                        value: Number(r.col2),              // degree (total contacts)
                                                                        highlightValue: Number(r.col8),    // contacts to other hubs
                                                                    }))
                                                                }
                                                                percentile={spmPercentile}
                                                                title="Contacts per residue · by SPM hub"
                                                                yAxisLabel="# of contacts"
                                                                allLabel="Total"
                                                                highlightLabel={`Top ${spmPercentile}% hubs`}
                                                                baseColor="#9dd9d2"
                                                                highlightColor="#1e9e8a"
                                                                onResidueClick={openEgoNetwork}
                                                            />
                                                        </div>

                                                        {/* Contacts per residue — highlighting contacts via SPM edges (col7) */}
                                                        <div className="col-md-4">
                                                            <GroupedResidueBarChart
                                                                data={spmResult.tableData
                                                                    .filter((r) => r && r.col1 !== '__summary__')
                                                                    .map((r) => ({
                                                                        position: r.col1,
                                                                        value: Number(r.col2),              // degree (total contacts)
                                                                        highlightValue: Number(r.col7),    // contacts via SPM edges
                                                                    }))
                                                                }
                                                                percentile={spmPercentile}
                                                                title="Contacts per residue · by SPM edge"
                                                                yAxisLabel="# of contacts"
                                                                allLabel="Total"
                                                                highlightLabel={`Top ${spmPercentile}% SPM edges`}
                                                                baseColor="#b5d4f4"
                                                                highlightColor="#185fa5"
                                                                onResidueClick={openEgoNetwork}
                                                            />
                                                        </div>

                                                            {/* Betweenness centrality */}
                                                            <div className="col-md-4">
                                                                <BetweennessBarChart
                                                                    data={spmResult.tableData
                                                                        .filter((r) => r && r.col1 !== '__summary__')
                                                                        .map((r) => ({
                                                                            position: r.col1,
                                                                            betweenness: Number(r.col3),
                                                                            degree: Number(r.col2),
                                                                            isHub: r.col5 === 'Yes',
                                                                        }))
                                                                    }
                                                                    percentile={spmPercentile}
                                                                    title="Betweenness centrality"
                                                                    onResidueClick={openEgoNetwork}
                                                                />
                                                            </div>
                                                        </div>

                                                        {/* SPM profile summary + table */}
                                                        <div className="mb-4">
                                                            <h4 className="mb-3">
                                                                <i className="pi pi-sitemap me-2 text-primary" />
                                                                SPM profile
                                                            </h4>
                                                            <SpmProfileTable
                                                                tableData={spmResult.tableData}
                                                                percentile={spmPercentile}
                                                                onSelectResidue={openEgoNetwork}
                                                                loading={analysisLoading && analysisMode === 'spm'}
                                                            />
                                                        </div>

                                                        {/* Ego network drill-in */}
                                                        {egoResidue && (
                                                            <div className="mt-4 animate__animated animate__fadeIn">
                                                                {egoLoading ? (
                                                                    <Card className="text-center py-5">
                                                                        <i className="pi pi-spin pi-spinner text-primary" style={{ fontSize: '2rem' }} />
                                                                        <p className="mt-3 mb-0 text-secondary">
                                                                            Loading ego network for {egoResidue}…
                                                                        </p>
                                                                    </Card>
                                                                ) : egoData && egoData.neighbors && egoData.neighbors.length > 0 ? (
                                                                    <EgoNetworkPlot
                                                                        center={egoResidue}
                                                                        neighbors={egoData.neighbors}
                                                                        percentile={spmPercentile}
                                                                        onClose={() => {
                                                                            setEgoResidue(null);
                                                                            setEgoData(null);
                                                                        }}
                                                                        title="Residue neighborhood"
                                                                    />
                                                                ) : (
                                                                    <Message
                                                                        severity="info"
                                                                        className="w-full"
                                                                        text={`No neighbors within 8 Å of ${egoResidue}.`}
                                                                    />
                                                                )}
                                                            </div>
                                                        )}
                                                    </div>
                                                )}

                                                {spmResult && spmResult.resultType === 'text' && (
                                                    <Message
                                                        severity="warn"
                                                        className="w-full mt-4"
                                                        text={spmResult.tableData?.message || spmResult.text || 'No SPM result found.'}
                                                    />
                                                )}
                                            </TabPanel>

                                            {/* ============================================= */}
                                            {/* TAB 3 — Functional annotation   */}
                                            {/* ============================================= */}
                                            <TabPanel header="More Analyses" leftIcon="pi pi-th-large me-2">

                                                        {/* level4 — Ligand–Variant */}
                                                        <div className="mb-4 pb-3 border-bottom">
                                                            <h5 className="mb-1">Ligand–variant overlap</h5>
                                                            <p className="text-secondary small mb-2">
                                                                Intersects UniProt ligand-binding events with ClinVar variants to flag functional ablation and potential drug-resistance hotspots.
                                                            </p>
                                                            <Button
                                                                label="Find overlaps"
                                                                icon="pi pi-link"
                                                                size="small"
                                                                onClick={() => runGraphAnalysis('level4')}
                                                                loading={analysisLoading && analysisMode === 'level4'}
                                                            />
                                                            {genericResults.level4 && (
                                                                <div className="mt-3">
                                                                    {genericResults.level4.resultType === 'text' ? (
                                                                        <Message severity="warn" className="w-full"
                                                                            text={genericResults.level4.text || 'No overlaps found.'} />
                                                                    ) : (
                                                                        <GenericAnalysisResult
                                                                            mode="level4"
                                                                            tableData={genericResults.level4.tableData}
                                                                        />
                                                                    )}
                                                                </div>
                                                            )}
                                                        </div>

                                                        {/* level6 — PTM crosstalk */}
                                                        <div className="mb-4 pb-3 border-bottom">
                                                            <h5 className="mb-1">PTM crosstalk (3D)</h5>
                                                            <p className="text-secondary small mb-2">
                                                                Pairs of post-translationally modified residues that sit
                                                                within <strong>8 Å</strong> in 3D — candidates for
                                                                regulatory crosstalk.
                                                            </p>
                                                            <Button
                                                                label="Find PTM pairs"
                                                                icon="pi pi-sync"
                                                                size="small"
                                                                onClick={() => runGraphAnalysis('level6')}
                                                                loading={analysisLoading && analysisMode === 'level6'}
                                                            />
                                                            {genericResults.level6 && (
                                                                <div className="mt-3">
                                                                    {genericResults.level6.resultType === 'text' ? (
                                                                        <Message severity="warn" className="w-full"
                                                                            text={genericResults.level6.text || 'No PTM pairs.'} />
                                                                    ) : (
                                                                        <GenericAnalysisResult
                                                                            mode="level6"
                                                                            tableData={genericResults.level6.tableData}
                                                                        />
                                                                    )}
                                                                </div>
                                                            )}
                                                        </div>

                                                        {/* level7 — PTM hotspots */}
                                                        <div className="mb-4 pb-3 border-bottom">
                                                            <h5 className="mb-1">PTM hotspots (sequence)</h5>
                                                            <p className="text-secondary small mb-2">
                                                                Sequence windows of ±15 residues enriched in PTMs
                                                                (Fisher exact test with Benjamini–Hochberg FDR, α = 0.05).
                                                            </p>
                                                            <Button
                                                                label="Find hotspots"
                                                                icon="pi pi-chart-line"
                                                                size="small"
                                                                onClick={() => runGraphAnalysis('level7')}
                                                                loading={analysisLoading && analysisMode === 'level7'}
                                                            />
                                                            {genericResults.level7 && (
                                                                <div className="mt-3">
                                                                    {genericResults.level7.resultType === 'text' ? (
                                                                        <Message severity="warn" className="w-full"
                                                                            text={genericResults.level7.text || 'No hotspots.'} />
                                                                    ) : (
                                                                        <GenericAnalysisResult
                                                                            mode="level7"
                                                                            tableData={genericResults.level7.tableData}
                                                                        />
                                                                    )}
                                                                </div>
                                                            )}
                                                        </div>

                                                        {/* level9 — PTM–Variant */}
                                                        <div className="mb-4 pb-3 border-bottom">
                                                            <h5 className="mb-1">PTM–variant disruption</h5>
                                                            <p className="text-secondary small mb-2">
                                                                Pathogenic ClinVar variants that either hit a PTM site
                                                                directly (<em>direct abolition</em>) or lie within 8 Å
                                                                of one (<em>steric/electrostatic disruption</em>).
                                                            </p>
                                                            <Button
                                                                label="Find disruptions"
                                                                icon="pi pi-bolt"
                                                                size="small"
                                                                onClick={() => runGraphAnalysis('level9')}
                                                                loading={analysisLoading && analysisMode === 'level9'}
                                                            />
                                                            {genericResults.level9 && (
                                                                <div className="mt-3">
                                                                    {genericResults.level9.resultType === 'text' ? (
                                                                        <Message severity="warn" className="w-full"
                                                                            text={genericResults.level9.text || 'No disruptions.'} />
                                                                    ) : (
                                                                        <GenericAnalysisResult
                                                                            mode="level9"
                                                                            tableData={genericResults.level9.tableData}
                                                                        />
                                                                    )}
                                                                </div>
                                                            )}
                                                        </div>

                                                        {/* level12 — VUS resolver */}
                                                        <div>
                                                            <h5 className="mb-1">VUS resolver</h5>
                                                            <p className="text-secondary small mb-2">
                                                                For a residue of interest (often a variant of uncertain
                                                                significance), lists its 3D neighbors with functional
                                                                context — pathogenic variants nearby, ligand binding,
                                                                PTMs, domain membership.
                                                            </p>
                                                            <div className="row g-2 align-items-end">
                                                                <div className="col-md-6">
                                                                    <label className="form-label small">Target residue</label>
                                                                    <InputText
                                                                        className="w-100"
                                                                        placeholder="e.g. P04001_155"
                                                                        value={vusTarget}
                                                                        onChange={(e) => setVusTarget(e.target.value)}
                                                                    />
                                                                </div>
                                                                <div className="col-md-3">
                                                                    <Button
                                                                        className="w-100"
                                                                        label="Resolve"
                                                                        icon="pi pi-search"
                                                                        size="small"
                                                                        disabled={!vusTarget.trim()}
                                                                        onClick={() => runGraphAnalysis('level12', {
                                                                            startResidue: vusTarget.trim(),
                                                                        })}
                                                                        loading={analysisLoading && analysisMode === 'level12'}
                                                                    />
                                                                </div>
                                                            </div>
                                                            {genericResults.level12 && (
                                                                <div className="mt-3">
                                                                    {genericResults.level12.resultType === 'text' ? (
                                                                        <Message severity="warn" className="w-full"
                                                                            text={genericResults.level12.text || 'No neighbors.'} />
                                                                    ) : (
                                                                        <GenericAnalysisResult
                                                                            mode="level12"
                                                                            tableData={genericResults.level12.tableData}
                                                                        />
                                                                    )}
                                                                </div>
                                                            )}
                                                        </div>

                                            </TabPanel>
                                        </TabView>
                                    </Card>
                                </div>
                            </div>
                        </>
                    )}
                </div>
            </section>
            <Footer />
        </>
    )
}

export default CalculationPage;